import { useCallback, useReducer } from 'react';

const MAX_PAST = 80;

type Hist<T> = { past: T[]; present: T; future: T[] };

type Act<T> =
  | { type: 'set'; payload: T | ((p: T) => T) }
  | { type: 'undo' }
  | { type: 'redo' }
  | { type: 'reset'; payload: T };

function reduceHist<T>(state: Hist<T>, action: Act<T>): Hist<T> {
  switch (action.type) {
    case 'reset':
      return { past: [], present: action.payload, future: [] };
    case 'set': {
      const next = typeof action.payload === 'function' ? (action.payload as (p: T) => T)(state.present) : action.payload;
      if (next === state.present) return state;
      return {
        past: [...state.past, state.present].slice(-MAX_PAST),
        present: next,
        future: [],
      };
    }
    case 'undo': {
      if (state.past.length === 0) return state;
      const prev = state.past[state.past.length - 1]!;
      return {
        past: state.past.slice(0, -1),
        present: prev,
        future: [state.present, ...state.future],
      };
    }
    case 'redo': {
      if (state.future.length === 0) return state;
      const n = state.future[0]!;
      return {
        past: [...state.past, state.present].slice(-MAX_PAST),
        present: n,
        future: state.future.slice(1),
      };
    }
    default:
      return state;
  }
}

export interface UndoRedoState<T> {
  state: T;
  setState: (updater: T | ((prev: T) => T)) => void;
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  reset: (next: T) => void;
}

export function useUndoRedo<T>(initialState: T): UndoRedoState<T> {
  const [h, dispatch] = useReducer(reduceHist<T>, {
    past: [],
    present: initialState,
    future: [],
  } as Hist<T>);

  const setState = useCallback((updater: T | ((prev: T) => T)) => {
    dispatch({ type: 'set', payload: updater });
  }, []);

  const undo = useCallback(() => dispatch({ type: 'undo' }), []);
  const redo = useCallback(() => dispatch({ type: 'redo' }), []);
  const reset = useCallback((next: T) => dispatch({ type: 'reset', payload: next }), []);

  return {
    state: h.present,
    setState,
    undo,
    redo,
    canUndo: h.past.length > 0,
    canRedo: h.future.length > 0,
    reset,
  };
}
